import QrReader from "react-qr-reader";
import Edit from "@material-ui/icons/Edit";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import Camera from "@material-ui/icons/Camera";
import TextField from "@material-ui/core/TextField";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import { useState } from "react";
import { Package } from "shared/types";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import { moveToStorage } from "shared/network/package.api";
import { useDebouncedCallback } from "use-debounce";
import { useSelector } from "react-redux";
import { RootState } from "config/store";

type Props = {
  pack?: Package | null;
  setSelectedStorageCode: (selected: Package | null) => void;
  refetch: () => void;
  onSuccess?: () => void;
};

type StatusUpdate = {
  storageCode: string;
};

const StorageCodeModal = ({ pack, setSelectedStorageCode, refetch, onSuccess }: Props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [qrOpen, setQrOpen] = useState(false);
  const form = useForm<StatusUpdate>({ shouldUnregister: false });
  const companyId = useSelector((state: RootState) => state.application.currentCompany?.id);

  // Csak tájékoztatásként jelenik meg, a régi tárhelyről a szerver veszi le a csomagot
  const currentStorageCode =
    pack?.actualStatus?.relPackagesStatusStorages?.[0]?.storage?.storageCode;

  const onClose = () => {
    setSelectedStorageCode(null);
    setQrOpen(false);
    form.reset();
  };

  const handleScan = (data: string | null) => {
    if (data) {
      form.setValue("storageCode", data);
      setQrOpen(false);
    }
  };

  const handleError = (err: any) => {
    console.error(err);
  };

  const onSubmit = async (values: StatusUpdate) => {
    handleChange.cancel();
    if (!pack) {
      return;
    }
    try {
      await moveToStorage({
        storageCode: values.storageCode.trim(),
        type: "PACKAGE",
        packageIds: [pack.id],
        companyId,
      });
      enqueueSnackbar(t("package.moveSuccess"), { variant: "success" });
      onClose();
      refetch();
      onSuccess?.();
    } catch (error) {
      enqueueSnackbar(t("package.moveFailure"), { variant: "error" });
      form.reset();
    }
  };

  const handleChange = useDebouncedCallback((value: string) => {
    if (value) {
      form.handleSubmit(onSubmit)();
    }
  }, 300);

  return (
    <Dialog open={!!pack} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{`${t("package.storageCodeTitle")} (${pack?.trackingNumber})`}</DialogTitle>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <DialogContent>
            <TextField
              label={t("package.currentStorageCode")}
              value={currentStorageCode || ""}
              InputLabelProps={{ shrink: true }}
              disabled
            />
            <>
              {!qrOpen && (
                <>
                  <TextField
                    label={t("package.newStorageCode")}
                    name="storageCode"
                    autoFocus
                    InputLabelProps={{ shrink: true, required: true }}
                    inputRef={form.register({
                      required: {
                        value: true,
                        message: t("validation:required"),
                      },
                    })}
                    error={form.errors?.storageCode && true}
                    helperText={form.errors?.storageCode?.message}
                    onChange={e => handleChange(e.target.value)}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<Camera />}
                    onClick={() => setQrOpen(true)}
                    style={{
                      marginTop: 12,
                      marginBottom: 8,
                      width: "100%",
                    }}
                  >
                    {t("package.qrCodeButton")}
                  </Button>
                </>
              )}
              {qrOpen && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => setQrOpen(false)}
                    style={{
                      marginBottom: 8,
                      width: "100%",
                    }}
                  >
                    {t("package.storageNumberButton")}
                  </Button>
                  <QrReader
                    delay={100}
                    resolution={100}
                    onError={handleError}
                    onScan={handleScan}
                  />
                </>
              )}
            </>
          </DialogContent>
          <DialogActions>
            <Button color="primary" variant="text" onClick={() => onClose()}>
              {t("common:button.cancel")}
            </Button>
            <Button type="submit" color="primary" variant="contained">
              {t("common:button.save")}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default StorageCodeModal;
